import React, { useState, useCallback, useEffect } from 'react';
import { X } from 'lucide-react';

/* ------------------------------------------------------------------ */
/*  Types                                                             */
/* ------------------------------------------------------------------ */

interface FirstRunTourProps {
  isOpen: boolean;
  onComplete: () => void;
}

interface TourStep {
  title: string;
  description: string;
  position: { top: string; left: string };
  arrowPosition: 'top' | 'bottom' | 'left' | 'right';
}

/* ------------------------------------------------------------------ */
/*  Steps                                                             */
/* ------------------------------------------------------------------ */

const TOUR_STEPS: TourStep[] = [
  {
    title: 'Your business at a glance',
    description:
      'This dashboard gives you a real-time overview of revenue, conversations, and campaign performance.',
    position: { top: '80px', left: '50%' },
    arrowPosition: 'top',
  },
  {
    title: 'Reach, Convert, Retain',
    description:
      'Use the sidebar to navigate between marketing campaigns, customer support, and retention flows.',
    position: { top: '240px', left: '260px' },
    arrowPosition: 'left',
  },
  {
    title: 'AI Copilot is always available',
    description:
      'Click the copilot button in the bottom-right corner to ask questions, get suggestions, or run actions.',
    position: { top: 'calc(100vh - 220px)', left: 'calc(100vw - 380px)' },
    arrowPosition: 'bottom',
  },
  {
    title: 'Search anything with Ctrl+K',
    description:
      'Quickly find customers, campaigns, conversations, and settings using the global search shortcut.',
    position: { top: '80px', left: 'calc(50% + 60px)' },
    arrowPosition: 'top',
  },
];

const STORAGE_KEY = 'tour_completed';

/* ------------------------------------------------------------------ */
/*  Component                                                         */
/* ------------------------------------------------------------------ */

export default function FirstRunTour({ isOpen, onComplete }: FirstRunTourProps) {
  const [currentStep, setCurrentStep] = useState(0);

  // Check if already completed
  useEffect(() => {
    if (isOpen && localStorage.getItem(STORAGE_KEY) === 'true') {
      onComplete();
    }
  }, [isOpen, onComplete]);

  const finish = useCallback(() => {
    localStorage.setItem(STORAGE_KEY, 'true');
    onComplete();
  }, [onComplete]);

  const handleNext = useCallback(() => {
    if (currentStep < TOUR_STEPS.length - 1) {
      setCurrentStep((s) => s + 1);
    } else {
      finish();
    }
  }, [currentStep, finish]);

  if (!isOpen) return null;

  const step = TOUR_STEPS[currentStep];

  // Arrow CSS per position
  const arrowStyles: Record<string, string> = {
    top: 'before:absolute before:-top-2 before:left-6 before:w-4 before:h-4 before:bg-gray-900 before:rotate-45 before:rounded-sm',
    bottom:
      'before:absolute before:-bottom-2 before:left-6 before:w-4 before:h-4 before:bg-gray-900 before:rotate-45 before:rounded-sm',
    left: 'before:absolute before:top-6 before:-left-2 before:w-4 before:h-4 before:bg-gray-900 before:rotate-45 before:rounded-sm',
    right:
      'before:absolute before:top-6 before:-right-2 before:w-4 before:h-4 before:bg-gray-900 before:rotate-45 before:rounded-sm',
  };

  return (
    <>
      {/* Backdrop overlay */}
      <div className="fixed inset-0 bg-black/30 z-[9998]" />

      {/* Tooltip */}
      <div
        className={`fixed z-[9999] w-80 rounded-xl bg-gray-900 text-white p-5 shadow-2xl ${arrowStyles[step.arrowPosition]}`}
        style={{
          top: step.position.top,
          left: step.position.left,
          transform: 'translateX(-50%)',
        }}
      >
        {/* Close button */}
        <button
          onClick={finish}
          className="absolute top-3 right-3 text-gray-400 hover:text-white transition-colors"
          aria-label="Close tour"
        >
          <X className="w-4 h-4" />
        </button>

        <h3 className="text-base font-semibold mb-1">{step.title}</h3>
        <p className="text-sm text-gray-300 mb-4 leading-relaxed">{step.description}</p>

        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-500">
            {currentStep + 1} of {TOUR_STEPS.length}
          </span>

          <div className="flex items-center gap-3">
            <button
              onClick={finish}
              className="text-xs text-gray-400 hover:text-white transition-colors"
            >
              Skip Tour
            </button>
            <button
              onClick={handleNext}
              className="px-4 py-1.5 text-sm font-medium rounded-lg bg-violet-600 hover:bg-violet-700 transition-colors"
            >
              {currentStep === TOUR_STEPS.length - 1 ? 'Finish' : 'Next'}
            </button>
          </div>
        </div>

        {/* Step dots */}
        <div className="flex items-center gap-1.5 mt-3 justify-center">
          {TOUR_STEPS.map((_, i) => (
            <span
              key={i}
              className={`block w-1.5 h-1.5 rounded-full transition-colors ${
                i === currentStep ? 'bg-violet-400' : 'bg-gray-600'
              }`}
            />
          ))}
        </div>
      </div>
    </>
  );
}
